import React, { MutableRefObject, useEffect, useState } from "react";
import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import dayjs from "dayjs";

import style from "./index.module.css";

import GetImages from "@/components/GetImages";
import GenericSuccessAlert from "@/components/GenericSuccessAlert";
import ScheduleEventPopUps from "@/components/ScheduleEventPopUps";
import RescheduleAppointment from "@/components/MakeAppointment";

import {
  capitalizeFirstLetter,
  getLastMessageTime,
  getLatestTimestampFunc,
  timeInAmPmFormat,
  transformClasses,
} from "@/utils";
import useRescheduleCancelledAppointments from "@/utils/hooks/useRescheduleCancelledAppointments";
import { INotifyEnum, notify } from "@/utils/toaster";

import {
  IAppointmentCardDetailEnum,
  IAppointmentCreation,
  IAppointmetEnums,
  IDoctorAvailabilityPlatform,
  IDoctorScheduleConsultationType,
  IMakeAppointmentEvent,
  IPatientAppointmentStatus,
  IWebPanelType,
} from "@/models/doctor";
import { IAppointments } from "@/models/ApiRequestResponse/doctor";
import { ISessionKeys } from "@/models";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setCreateAndResetPrescriptionTemplateDetail,
  setResetPrescriptionTemplateValues,
  setSelectedTemplate,
} from "@/store/slices/doctor/prescription";
import { setAppointmentDetail } from "@/store/slices/doctor/appointment";
import {
  fetchScheduleAppointmentList,
  setAppointmentSelection,
} from "@/store/slices/doctor/schedule";
import { setVideoAppointmentDetail } from "@/store/slices/videoAppointment";
import { config, setIsShowUniversalSearch } from "@/store/slices/config";
import { chat, setSelectedChat } from "@/store/slices/chat";
import {
  setScheduleFilter,
  universalSearchFilterDetails,
} from "@/store/slices/doctor/universalSearchFilter";

import { useFirebaseMessagingContext } from "@/context/Chat";

import { storage } from "@/utils/Storage";

const appointmentStatusMap = {
  [IPatientAppointmentStatus.COMPLETED]: transformClasses(
    style.status,
    style.statusCompleted
  ),
  [IPatientAppointmentStatus.CANCELLED]: transformClasses(
    style.status,
    style.statusCancelled
  ),
  [IPatientAppointmentStatus.PENDING]: style.status,
};

interface IMobile {
  isMobileOrTablet: boolean;
  type?: IAppointmentCardDetailEnum;
  webPanelType?: IWebPanelType;
  clinicName?: string;
  appointmentDate?: string;
  setUnreadCount?: (value: number) => void;
  newMessages?: MutableRefObject<number | null>;
}
type IProps = IAppointments &
  IMobile & {
    isSelected?: boolean;
  };
const AppointmentDetailsView: React.FC<IProps> = (props) => {
  const {
    id,
    type_of_consultation,
    patient,
    slotStartTime,
    slotNo,
    status,
    slotDuration,
    activePrescription,
    clinic_id,
    isMobileOrTablet,
    weekday,
    isSelected = false,
    webPanelType = IWebPanelType.SCHEDULE,
    type = IAppointmentCardDetailEnum.LIST,
    paymentStatus,
    consultationFee,
    discountAmount,
    finalAmount,
    paymentMethod,
    paymentMethodId,
    clinicName,
    appointmentDate,
    blockedPatient,
    scheduleId,
    date,
    setUnreadCount,
    newMessages,
  } = props;

  const [rescheduleAppointment, setRescheduleAppointment] =
    useState<IAppointmentCreation>({
      creation: false,
      success: false,
    });
  const [isSchedulePopUp, setSchedulePopUp] = useState({
    isOpen: false,
    detail: {
      platform: IDoctorAvailabilityPlatform.VIDEO_CALL,
      status: IPatientAppointmentStatus.COMPLETED,
    },
  });
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [chatDetail, setChatDetail] = useState<any>(null);

  const { fetchConversationById, listType, channelId, paginatedDocs } =
    useFirebaseMessagingContext();

  const currentUserId = storage.getSessionToken(ISessionKeys.USER_ID);

  const { rescheduleCancelledAppointments, rescheduleSlotNo } =
    useRescheduleCancelledAppointments();

  const dispatch = useAppDispatch();
  const { selection } = useAppSelector((state) => state.schedule);
  const { appointmentDetail, makePatientAppointment } = useAppSelector(
    (state) => state.appointment
  );
  const { isShowUniversalSearch } = useAppSelector(config);
  const { selectedChat } = useAppSelector(chat);
  const { schedule } = useAppSelector(universalSearchFilterDetails);

  const isPending = status === IPatientAppointmentStatus.PENDING;
  const isCancelled = status === IPatientAppointmentStatus.CANCELLED;
  const isCompleted = status === IPatientAppointmentStatus.COMPLETED;
  const isVideoCall =
    type_of_consultation === IDoctorScheduleConsultationType.VIDEO_CALL;
  const isClinicVisit =
    type_of_consultation === IDoctorScheduleConsultationType.IN_CLINIC;

  const isCancelledOrCompleted = isCancelled || isCompleted;

  const handleScheduleStatusPopUps = (
    status: IPatientAppointmentStatus,
    platform: IDoctorScheduleConsultationType
  ): void => {
    setSchedulePopUp({
      isOpen: true,
      detail: {
        status: status,
        platform:
          platform === IDoctorScheduleConsultationType.VIDEO_CALL
            ? IDoctorAvailabilityPlatform.VIDEO_CALL
            : IDoctorAvailabilityPlatform.IN_CLINIC,
      },
    });
  };

  const handleScheduleDetail = (): void => {
    if (isMobileOrTablet) {
      handleScheduleStatusPopUps(
        status,
        type_of_consultation ?? IDoctorScheduleConsultationType.VIDEO_CALL
      );
    } else if (isShowUniversalSearch) {
      dispatch(setIsShowUniversalSearch(false));
      if (!(schedule?.startDate && schedule?.endDate)) {
        const startDate = dayjs().format("YYYY-MM-DD");
        const endDate = dayjs(date).add(1, "day").format("YYYY-MM-DD");
        dispatch(
          fetchScheduleAppointmentList({
            start: startDate,
            end: endDate,
          })
        );
        dispatch(
          setScheduleFilter({
            startDate,
            endDate,
          })
        );
      }
    }

    fetchConversationById(id).then((response) =>
      dispatch(setSelectedChat(response))
    );

    dispatch(
      setAppointmentDetail({
        id,
        type_of_consultation,
        patient,
        slotStartTime,
        slotNo,
        status,
        slotDuration,
        activePrescription,
        clinic_id,
        consultationFee,
        discountAmount,
        finalAmount,
        paymentMethod,
        paymentMethodId,
        paymentStatus,
        clinicName,
        appointmentDate,
        blockedPatient,
        scheduleId,
      })
    );
    dispatch(
      setVideoAppointmentDetail({
        id,
        type_of_consultation,
        patient,
        slotStartTime,
        slotNo,
        status,
        slotDuration,
        activePrescription,
        clinic_id,
      })
    );
    dispatch(setCreateAndResetPrescriptionTemplateDetail());
    dispatch(setResetPrescriptionTemplateValues());
    dispatch(setSelectedTemplate({}));
  };

  const handleAppointmentSelection = (isChecked: boolean): void => {
    dispatch(setAppointmentSelection({ [id]: isChecked }));
  };

  const closeRescheduleModal = (): void => {
    const rescheduleModal: IAppointmentCreation = {
      creation: false,
      success: false,
    };
    for (const key in rescheduleAppointment) {
      if (key in rescheduleModal) {
        rescheduleModal[key as keyof IAppointmentCreation] = false;
      }
    }
    setRescheduleAppointment(rescheduleModal);
  };

  const openAppointmentRescheduleDetailHandler = async (
    type: IAppointmetEnums
  ) => {
    switch (type) {
      case IAppointmetEnums.CREATION:
        setRescheduleAppointment({
          ...rescheduleAppointment,
          creation: true,
        });
        return;
      case IAppointmetEnums.SUCCESS:
        const response = await rescheduleCancelledAppointments();
        if (response && "message" in response) {
          notify(INotifyEnum.ERROR, response?.message);
          return;
        }
        setRescheduleAppointment({
          ...rescheduleAppointment,
          success: true,
        });
        closeSchedulePopUpHandler();
        return;
      default:
        closeRescheduleModal();
        return;
    }
  };

  const closeSchedulePopUpHandler = (): void => {
    setSchedulePopUp({ ...isSchedulePopUp, isOpen: false });
  };

  useEffect(() => {
    fetchConversationById(id).then((response) => setChatDetail(response));
  }, [id, listType, channelId, selectedChat, paginatedDocs]);

  useEffect(() => {
    if (newMessages) {
      newMessages.current = 0;
      fetchConversationById(id).then((response) => {
        const isStaffInvolved =
          response?.users?.staff_members?.includes(currentUserId) &&
          response?.users?.staff_members?.includes(
            response?.lastMessage?.patient?.sender_id
          );
        const lastMsgInternal = response?.lastMessage?.internal;
        const lastMsInternalSeen = lastMsgInternal?.seen;
        const lastMsgPatient = response?.lastMessage?.patient;
        const lastMsgPatientSeen = lastMsgPatient?.seen;

        if (
          lastMsInternalSeen &&
          lastMsInternalSeen[currentUserId]?.unReadMessageCount > 0 &&
          lastMsgInternal?.sender_id !== currentUserId
        ) {
          newMessages.current +=
            lastMsInternalSeen &&
            lastMsInternalSeen[currentUserId]?.unReadMessageCount
              ? 1
              : 0;
        } else if (
          lastMsgPatientSeen &&
          lastMsgPatientSeen[currentUserId]?.unReadMessageCount > 0 &&
          !isStaffInvolved
        ) {
          newMessages.current +=
            lastMsgPatientSeen &&
            lastMsgPatientSeen[currentUserId]?.unReadMessageCount
              ? 1
              : 0;
        }
        if (setUnreadCount) {
          setUnreadCount(newMessages.current);
        }
      });
    }
  }, [id, paginatedDocs]);

  const renderAppointmentStatus = (): JSX.Element => {
    return (
      <Grid
        item
        display={"flex"}
        flexDirection={"column"}
        alignItems={"flex-end"}
      >
        <Typography
          component={"p"}
          className={
            (isPending || isCancelled || isCompleted) && !activePrescription
              ? transformClasses(style.statusText, appointmentStatusMap[status])
              : {}
          }
          onClick={handleScheduleDetail}
        >
          {status === IPatientAppointmentStatus.PENDING
            ? activePrescription && (
                <GetImages
                  name={"PrescriptionDefaultSmallIcon"}
                  width={"24"}
                  height={"19"}
                />
              )
            : capitalizeFirstLetter(status)}
        </Typography>
        {!isMobileOrTablet &&
          (webPanelType === IWebPanelType.RECEPTION ||
            webPanelType === IWebPanelType.SCHEDULE) && (
            <Grid
              item
              display={"flex"}
              alignItems={"center"}
              justifyContent={"end"}
              marginTop={1}
            >
              <Typography component={"p"} className={style.paymentStatus}>
                {capitalizeFirstLetter(paymentStatus)}
              </Typography>
            </Grid>
          )}

        {!isMobileOrTablet &&
          webPanelType === IWebPanelType.SCHEDULE &&
          (() => {
            const lastMsgInternal = chatDetail?.lastMessage?.internal;
            const lastMsgInternalSeen = lastMsgInternal?.seen;
            const lastMsgPatients = chatDetail?.lastMessage?.patient;
            const lastMsgPatientSeen = lastMsgPatients?.seen;

            const isStaffInvolved =
              chatDetail?.users?.staff_members?.includes(currentUserId) &&
              chatDetail?.users?.staff_members?.includes(
                lastMsgPatients?.sender_id
              );

            const lastMsgPatient = isStaffInvolved ? null : lastMsgPatients;

            const hasMentions =
              currentUserId !== lastMsgInternal?.sender_id &&
              lastMsgInternalSeen &&
              lastMsgInternalSeen[currentUserId]?.mentions?.length > 0;

            let unreadMessageCount = 0;

            if (
              lastMsgInternalSeen &&
              lastMsgInternalSeen[currentUserId]?.unReadMessageCount > 0 &&
              lastMsgInternal?.sender_id !== currentUserId
            ) {
              unreadMessageCount =
                (lastMsgInternalSeen &&
                  lastMsgInternalSeen[currentUserId]?.unReadMessageCount) ||
                0;
            } else if (
              lastMsgPatientSeen &&
              lastMsgPatientSeen[currentUserId]?.unReadMessageCount > 0 &&
              !isStaffInvolved
            ) {
              unreadMessageCount =
                (lastMsgPatientSeen &&
                  lastMsgPatientSeen[currentUserId]?.unReadMessageCount) ||
                0;
            }

            const isUnread =
              unreadMessageCount > 0 &&
              (lastMsgInternal?.sender_id !== currentUserId ||
                lastMsgPatient?.sender_id !== currentUserId);

            const latestTimestamp = getLatestTimestampFunc(
              chatDetail?.lastMessage?.internal,
              lastMsgPatients
            );

            const lastMessageTime = latestTimestamp
              ? getLastMessageTime(latestTimestamp)
              : null;

            if (hasMentions) {
              return (
                <Grid
                  item
                  display="flex"
                  alignItems="center"
                  sx={{ marginTop: "0.6rem" }}
                >
                  <Typography component="p" className={style.messageTime}>
                    {lastMessageTime}
                  </Typography>
                  <Grid item className={style.messageStartMentionUnReadCount}>
                    <Grid item>
                      <GetImages
                        name="StarMentionIcon"
                        width="11"
                        height="10"
                      />
                    </Grid>
                    <Typography component="p">
                      {lastMsgInternal?.mentionCount ??
                        lastMsgInternal?.mentions?.length ??
                        0}
                    </Typography>
                  </Grid>
                </Grid>
              );
            }

            if (isUnread) {
              return (
                <Grid
                  item
                  display="flex"
                  alignItems="center"
                  sx={{ marginTop: "0.6rem" }}
                >
                  <Typography component="p" className={style.messageTime}>
                    {lastMessageTime}
                  </Typography>
                  <Grid item className={style.messageUnReadCount}>
                    <Typography component="p">{unreadMessageCount}</Typography>
                  </Grid>
                </Grid>
              );
            }

            return null;
          })()}
      </Grid>
    );
  };

  useEffect(() => {
    if (rescheduleAppointment.creation) {
      setSelectedTab(
        isSchedulePopUp.detail?.platform ===
          IDoctorAvailabilityPlatform.VIDEO_CALL
          ? IMakeAppointmentEvent.VIDEO_CALL
          : IMakeAppointmentEvent.IN_CLINIC
      );
    }
  }, [rescheduleAppointment]);

  return (
    <Grid
      item
      className={transformClasses(
        style.patientDetailContainer,
        isSelected && style.active
      )}
    >
      <Grid item className={style.patientContainer}>
        {!isCancelledOrCompleted && selection.isSelectionOn && (
          <FormControlLabel
            sx={{ margin: 0 }}
            control={
              <Checkbox
                checked={!!selection.appointmentSelection[id]}
                onChange={(e) => handleAppointmentSelection(e.target.checked)}
                checkedIcon={
                  <GetImages name="TickOrangeIcon" width="24" height="24" />
                }
                icon={<GetImages name="RadioBtnIcon" width="24" height="24" />}
                sx={{ padding: 0 }}
              />
            }
            label={""}
          />
        )}
        <Grid
          item
          className={style.patientNameContainer}
          onClick={handleScheduleDetail}
        >
          <Grid item>
            <Typography component={"p"} className={style.patientName}>
              {patient?.name}
            </Typography>
            <Grid item className={style.slotRecordContainer}>
              <Typography
                component={"p"}
                className={transformClasses(
                  style.slotRecordNumber,
                  style.slotChip
                )}
              >
                {isVideoCall
                  ? timeInAmPmFormat(slotStartTime ?? "")
                  : `Slot ${slotNo}`}
              </Typography>
              <Typography component={"span"} className={style.slotRecordNumber}>
                {weekday
                  ? `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}`
                  : ""}
              </Typography>
              <Typography
                component={"span"}
                className={style.clinicRecordNumber}
              >
                {type === IAppointmentCardDetailEnum.SEARCH ? (
                  !isVideoCall ? (
                    "Clinic"
                  ) : null
                ) : (
                  <>
                    <Typography
                      component={"p"}
                      className={style.clinicRecordNumber}
                    >
                      {patient?.records}{" "}
                      <FormattedMessage id="records" defaultMessage="Records" />
                    </Typography>
                  </>
                )}
              </Typography>
            </Grid>
          </Grid>

          {isPending && isVideoCall ? (
            <>
              {webPanelType === IWebPanelType.SCHEDULE && (
                <Grid
                  item
                  className={
                    activePrescription
                      ? ""
                      : isMobileOrTablet
                      ? style.videoIconContainer
                      : ""
                  }
                >
                  {isMobileOrTablet && (
                    <GetImages
                      name={
                        activePrescription
                          ? "PrescriptionDefaultSmallIcon"
                          : isMobileOrTablet
                          ? "VideoWhiteIcon"
                          : ""
                      }
                      width={"20"}
                      height={"20"}
                    />
                  )}
                  {!isMobileOrTablet && renderAppointmentStatus()}
                </Grid>
              )}
              {webPanelType === IWebPanelType.RECEPTION &&
                !isMobileOrTablet &&
                renderAppointmentStatus()}
            </>
          ) : (isPending && isClinicVisit) || isCompleted || isCancelled ? (
            <Grid item display={"flex"} alignItems={"center"}>
              {renderAppointmentStatus()}
            </Grid>
          ) : null}
        </Grid>
      </Grid>

      {isSchedulePopUp.isOpen && (
        <ScheduleEventPopUps
          openAppointmentSlotHandler={openAppointmentRescheduleDetailHandler}
          isOpen={isSchedulePopUp.isOpen}
          closeHandler={closeSchedulePopUpHandler}
          platform={isSchedulePopUp.detail?.platform}
          status={isSchedulePopUp.detail?.status}
        />
      )}
      {rescheduleAppointment.creation && (
        <RescheduleAppointment
          isReschedule={rescheduleAppointment.creation}
          isOpen={rescheduleAppointment.creation}
          platform={isSchedulePopUp.detail?.platform}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          closeScheduleModal={closeRescheduleModal}
          openScheduleModalHandler={openAppointmentRescheduleDetailHandler}
        />
      )}
      {rescheduleAppointment.success && (
        <GenericSuccessAlert
          type={isSchedulePopUp.detail?.platform}
          slotNumber={+rescheduleSlotNo}
          name={appointmentDetail?.patient?.name}
          age={appointmentDetail?.patient?.age.toString()}
          date={makePatientAppointment?.dateSlotTime}
          isOpen={rescheduleAppointment.success}
          closeScheduleModal={closeRescheduleModal}
        />
      )}
    </Grid>
  );
};

export default AppointmentDetailsView;
